import { useState } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import Link from 'src/components/ui/Link';
import Button from 'src/components/ui/Button';

export type ReportingPagerCurrent = 'home' | 'scope' | 'group';

interface BreadcrumbItem {
  name: string;
  path: string;
  id?: string;
}

interface ReportingPagerProps {
  current: ReportingPagerCurrent;
  scope?: { name: string; path: string; id?: string };
  group?: { name: string; path: string; id?: string; depth?: number };
  /** Intermediate breadcrumb items between scope and current group */
  intermediatePath?: BreadcrumbItem[];
  /** Path to go up one level */
  parentPath?: string;
  onDropCard?: (targetParentId: string) => void;
  isDragging?: boolean;
  showReportingHome?: boolean;
}

export default function ReportingPager({
  current,
  scope,
  group,
  intermediatePath = [],
  parentPath,
  onDropCard,
  isDragging = false,
  showReportingHome = true
}: ReportingPagerProps) {
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(targetId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    if (onDropCard) {
      onDropCard(targetId);
    }
  };

  const getDropZoneClass = (targetId: string) => {
    if (!isDragging) return '';
    const isOver = dragOverTarget === targetId;
    return `transition-all ${isOver ? 'bg-blue-50 border border-blue-300 rounded px-2 py-1 -mx-2 -my-1' : 'px-2 py-1 -mx-2 -my-1 border border-transparent'}`;
  };

  const renderBreadcrumbItem = (item: BreadcrumbItem) => {
    if (item.id && onDropCard) {
      return (
        <span
          key={item.id}
          className={getDropZoneClass(item.id)}
          onDragOver={(e) => handleDragOver(e, item.id!)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, item.id!)}
        >
          <Link to={item.path} className="hover:underline">
            {item.name}
          </Link>
          {isDragging && dragOverTarget === item.id && <span className="ml-1 text-xs text-blue-600">Move here</span>}
        </span>
      );
    }
    return (
      <Link key={item.path} to={item.path} className="hover:underline">
        {item.name}
      </Link>
    );
  };

  const hasAncestorNav =
    Boolean(parentPath) || showReportingHome || intermediatePath.length > 0 || (Boolean(scope) && current === 'group');

  if (!hasAncestorNav) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 py-2 reporting-pager">
      {/* Go up button - shown when not at root level, positioned on the left */}
      {parentPath && (
        <Link to={parentPath} className="flex-shrink-0">
          <Button variant="outline" size="md" icon={ArrowLeftIcon} title="Go up one level">
            Back
          </Button>
        </Link>
      )}

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-sm text-content-subtle flex-1 min-w-0">
        <span className="inline-flex items-center gap-1 flex-wrap">
          {showReportingHome &&
            (current === 'home' ? (
              <span className="text-content-default">Reporting</span>
            ) : (
              <Link to="/reporting" className="hover:underline">
                Reporting
              </Link>
            ))}
          {scope && (
            <>
              {showReportingHome && <span className="text-content-subtlest">/</span>}
              {current === 'scope' ? (
                <span className="text-content-default">{scope.name}</span>
              ) : (
                renderBreadcrumbItem({ name: scope.name, path: scope.path, id: scope.id })
              )}
            </>
          )}
          {intermediatePath.map((item) => (
            <span key={item.id || item.path} className="contents">
              <span className="text-content-subtlest">/</span>
              {renderBreadcrumbItem(item)}
            </span>
          ))}
          {group && (
            <>
              <span className="text-content-subtlest">/</span>
              <span className="text-content-default">{group.name}</span>
            </>
          )}
        </span>
      </nav>
    </div>
  );
}
